/**
 * Toast Component
 * Toast notification display component with Alpine.js
 */

import Alpine from 'alpinejs'
import type { ToastType } from '../types'

// ============================================================================
// Toast Container Component
// ============================================================================

/**
 * Toast container that displays all toast notifications
 */
export function toastContainer() {
  return {
    /**
     * Get toast icon sprite ID based on type.
     * Returns full sprite IDs (e.g. 'i-check-circle') so callers can build
     * `#${id}` URLs without further mapping.
     * Per decision D4 the warning variant reuses the error glyph.
     */
    getIcon(type: ToastType): string {
      const icons: Record<ToastType, string> = {
        success: 'i-check-circle',
        error: 'i-error',
        warning: 'i-error',
        info: 'i-info',
      }
      return icons[type]
    },

    /**
     * Get toast container class string based on type.
     * Returns the new `.toast` + variant alias; left-border accent is keyed
     * by the variant class in main.css.
     */
    getBgClass(type: ToastType): string {
      const classes: Record<ToastType, string> = {
        success: 'toast toast-success',
        error: 'toast toast-error',
        warning: 'toast toast-warning',
        info: 'toast toast-info',
      }
      return classes[type]
    },

    /**
     * Get toast border color based on type
     */
    getBorderClass(type: ToastType): string {
      const classes: Record<ToastType, string> = {
        success: 'border-mint',
        error: 'border-coral',
        warning: 'border-coral',
        info: 'border-mustard',
      }
      return classes[type]
    },

    /**
     * Get toast icon color based on type
     */
    getIconClass(type: ToastType): string {
      const classes: Record<ToastType, string> = {
        success: 'text-mint',
        error: 'text-coral',
        warning: 'text-coral',
        info: 'text-mustard',
      }
      return classes[type]
    },

    /**
     * Dismiss a toast
     */
    dismiss(id: number): void {
      const store = Alpine.store('toasts') as { remove(id: number): void }
      store.remove(id)
    },
  }
}

// ============================================================================
// Individual Toast Item Component
// ============================================================================

/**
 * Individual toast item with animations
 */
export function toastItem() {
  return {
    visible: false,
    leaving: false,

    /**
     * Initialize toast with enter animation
     */
    init(): void {
      // Trigger enter animation
      requestAnimationFrame(() => {
        this.visible = true
      })
    },

    /**
     * Close toast with exit animation
     */
    close(id: number): void {
      this.leaving = true

      // Wait for animation to complete
      setTimeout(() => {
        const store = Alpine.store('toasts') as { remove(id: number): void }
        store.remove(id)
      }, 300)
    },

    /**
     * Get enter/leave transition classes
     */
    get transitionClasses(): string {
      if (this.leaving) {
        return 'transform translate-x-full opacity-0'
      }
      if (this.visible) {
        return 'transform translate-x-0 opacity-100'
      }
      return 'transform translate-x-full opacity-0'
    },
  }
}

// ============================================================================
// Toast Helper Functions
// ============================================================================

/**
 * Show a success toast (convenience function)
 */
export function showSuccess(message: string): void {
  const store = Alpine.store('toasts') as { success(message: string): void }
  store.success(message)
}

/**
 * Show an error toast (convenience function)
 */
export function showError(message: string): void {
  const store = Alpine.store('toasts') as { error(message: string): void }
  store.error(message)
}

/**
 * Show an info toast (convenience function)
 */
export function showInfo(message: string): void {
  const store = Alpine.store('toasts') as { info(message: string): void }
  store.info(message)
}

/**
 * Show a warning toast (convenience function)
 */
export function showWarning(message: string): void {
  const store = Alpine.store('toasts') as { warning(message: string): void }
  store.warning(message)
}

// ============================================================================
// Register Components
// ============================================================================

export function registerToastComponents(): void {
  Alpine.data('toastContainer', toastContainer)
  Alpine.data('toastItem', toastItem)
}

// ============================================================================
// Export helpers for use in other modules
// ============================================================================

export const toast = {
  success: showSuccess,
  error: showError,
  info: showInfo,
  warning: showWarning,
}
